"use client";

import { WebShopIcon } from "@/src/components/WebShopIcon";
import { IconButton, TextField as MuiTextField } from "@mui/material";
import { FieldValues, useController, useFormContext } from "react-hook-form";
import { TextFieldProps } from "../lib/type";
import { validationRules } from "@/src/shared/utils/validationRules";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { has } from "lodash";
import { TextFieldMaskInput } from "./TextFieldMaskInput";
import { maskFieldProps } from "@/src/shared/utils/mask";
import { useEffect, useMemo } from "react";

const getPatternForDocumentType = (documentType: string, defaultPattern: RegExp | undefined) => {
    if (documentType === "TRAVELLING_PASSPORT") return /^[A-Za-z]+$/;
    if (documentType === "PASSPORT") return /^[А-Яа-яЁёІіЇїЄє]+$/;
    return defaultPattern;
};

const usePatternForDocumentType = (name: string, defaultPattern: RegExp | undefined) => {
    const { watch, trigger, getValues } = useFormContext();
    const documentType = watch("documentType")?.value;

    useEffect(() => {
        getValues("passportSeries");
        //  && trigger("passportSeries");
    }, [documentType, trigger]);

    return useMemo(() => {
        return name === "passportSeries" ? getPatternForDocumentType(documentType, defaultPattern) : defaultPattern;
    }, [name, documentType, defaultPattern]);
}

export const TextField = <T extends FieldValues>({
    // type,
    hideLabel = false,
    labelCaption = "",
    size = "medium",
    label = "",
    helperText,
    InputProps,
    disabled,
    disableUnderline = true,
    control,
    name,
    defaultValue,
    hintIcon,
    onClick,
    inputProps,
    formatValue,
    toggleHintArea,
    closeHintArea,
    openHintArea,
    minLength,
    maxLength,
    required,
    pattern,
    patternErrorMessage,
    deps,
    maskType,
    onChange: onChangeController,
    className,
    readOnly,
    autoComplete,
    checkIPN,
    ...rest
}: TextFieldProps<T>) => {
    const computedPattern = usePatternForDocumentType(name, pattern);
    const {
        field: { onChange, value, ref, name: fieldName },
        fieldState: { error: fieldError },
    } = useController({
        control,
        name,
        defaultValue,
        rules: validationRules.TextInput({ minLength, maxLength, required, disabled, pattern: computedPattern, patternErrorMessage, deps, checkIPN }),
    });

    return (
        <MuiTextField
            InputProps={{
                disableUnderline,
                inputComponent: maskType && has(maskFieldProps, maskType) ? TextFieldMaskInput : undefined,
                inputProps: {
                    minLength,
                    maxLength,
                    maskType,
                    ...inputProps,
                },
                endAdornment: (
                    <>
                        {hintIcon && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleHintArea?.();
                                }}
                                className={cn(
                                    'px-2',
                                    {
                                    hidden: !hintIcon || InputProps?.endAdornment,
                                    "flex": hintIcon,
                                    "md:hidden": true,
                                })}
                            >
                                <WebShopIcon name={"questionCircle"} />
                            </IconButton>
                        )}
                        {InputProps?.endAdornment && <IconButton
                            className={cn('px-2', { hidden: hintIcon, flex: !hintIcon })}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            disabled={disabled || readOnly}
                        >
                            {InputProps?.endAdornment}
                        </IconButton>}
                    </>
                ),
                readOnly,
                ...InputProps,
            }}
            helperText={helperText || fieldError?.message}
            size={size}
            disabled={disabled}
            error={!!fieldError}
            label={hideLabel ? undefined : label}
            name={fieldName}
            onChange={(e) => {
                const value = e.target.value;
                const formattedValue = formatValue ? formatValue(value) : value;
                onChange(formattedValue)
                if (onChangeController) {
                    onChangeController(e)
                }
            }}
            value={(value?.value ? value.value : value) ?? ''}
            ref={ref}
            autoComplete={readOnly ? "off" : (autoComplete || undefined)}
            className={cn('group', { 'select-none': readOnly }, className)}
            {...rest}
        />
    );
};
